import React, { useState } from "react";

import { faCopy, faPlus } from "@fortawesome/free-solid-svg-icons";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Divider,
  Form,
  Grid,
  InlineAlert,
  Input,
  Link,
  Modal,
  Tag,
  TextArea,
} from "@samskip/frontend-components";
import { Path, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { nameof } from "ts-simple-nameof";

import { BookingTemplateConstants } from "@assets/constants";
import { BookingTemplateFormTypes } from "@assets/formTypes";
import {
  defaultTransportRequestForm,
  PartnerForm,
  TransportRequestForm,
} from "@assets/formTypes/bookingTemplates";
import { CommonTypes } from "@assets/types";
import { getErrorMessage } from "@assets/utilities";
import { BookingTemplatesYup } from "@assets/yupObjects";

const MAX_TRANSPORT_REQUEST_STOPS = 5;

/**
 * @returns The default transport request form but with an unique id.
 */
const getDefaultTransportRequestFormWithId = () => {
  const transportRequestFormWithId: TransportRequestForm = {
    ...defaultTransportRequestForm,
    Id: self.crypto.randomUUID(),
  };
  return transportRequestFormWithId;
};

/**
 *  Initializing function because React suggest passing an initialization function to not call the function at setState with each render
 */
const createDefaultFormValues = () => {
  return [getDefaultTransportRequestFormWithId()];
};

interface TransportRequestModalProps {
  formValues: TransportRequestForm[];
  partnerValues: PartnerForm | null;
  type: BookingTemplateConstants.TRANSFER_ADDRESS_TYPE;
  onClose: CommonTypes.CallbackFunction;
  title: string;
  onSave: (
    transportRequests: TransportRequestForm[],
    type: BookingTemplateConstants.TRANSFER_ADDRESS_TYPE
  ) => void;
}
const TransportRequestModal = ({
  formValues: defaultFormValues,
  partnerValues,
  type,
  onClose,
  title,
  onSave,
}: TransportRequestModalProps) => {
  const [formValues, setFormValues] = useState<TransportRequestForm[]>(
    defaultFormValues.length > 0 ? defaultFormValues : createDefaultFormValues
  );

  const [selectedStopId, setSelectedStopId] = useState<CommonTypes.UUID>(
    formValues[0].Id
  );

  const { t } = useTranslation();

  const {
    formState: { errors },
    handleSubmit,
    register,
    reset,
    setValue,
    watch,
  } = useForm({
    defaultValues: formValues[0],
    resolver: yupResolver(BookingTemplatesYup.transportRequestValidationSchema),
    mode: "onTouched",
  });

  const getFormValuesWithUpdatedCurrentStop = (
    data: BookingTemplateFormTypes.TransportRequestForm
  ) => {
    return formValues.map((stop) => (stop.Id === selectedStopId ? data : stop));
  };

  const saveCurrentStop = (
    data: BookingTemplateFormTypes.TransportRequestForm
  ) => {
    setFormValues(getFormValuesWithUpdatedCurrentStop(data));
  };

  const onSubmit = (data: TransportRequestForm) => {
    onSave(getFormValuesWithUpdatedCurrentStop(data), type);
    onClose();
  };

  const handleAddStop = () => {
    if (formValues.length < MAX_TRANSPORT_REQUEST_STOPS) {
      handleSubmit((data) => {
        const newStop = getDefaultTransportRequestFormWithId();
        saveCurrentStop(data);
        setFormValues((old) => [...old, newStop]);
        handleStopChange(newStop.Id);
      })();
    }
  };

  const handleDeleteStop = (stopId: CommonTypes.UUID) => {
    setFormValues((oldFormValues) =>
      oldFormValues.filter((stop) => stop.Id !== stopId)
    );
    if (stopId === selectedStopId) {
      handleStopChange(formValues[0].Id);
    }
  };

  const handleStopChangeWithSave = (id: CommonTypes.UUID) => {
    if (id !== selectedStopId) {
      handleSubmit((data) => {
        saveCurrentStop(data);
        handleStopChange(id);
      })();
    }
  };

  const handleStopChange = (stopId: CommonTypes.UUID) => {
    setSelectedStopId(stopId);
    const stop = formValues.find((stop) => stop.Id === stopId);
    if (stop) {
      reset(stop);
    } else {
      reset({
        ...defaultTransportRequestForm,
        Id: stopId,
      });
    }
  };

  const populateCityValueFromPartner = () => {
    let cityValue = defaultTransportRequestForm.City;
    if (
      partnerValues?.PartnerFillOutType ===
      BookingTemplateConstants.PARTNER_FILL_OUT_TYPE.NEW
    ) {
      cityValue =
        partnerValues.CityPort?.LocalName ?? defaultTransportRequestForm.City;
    } else {
      cityValue = partnerValues?.City ?? defaultTransportRequestForm.City;
    }
    setValue("City", cityValue, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const onCopyDetailsFromPartner = () => {
    const fieldsToPopulate = [
      nameof<TransportRequestForm>((i) => i.CompanyName),
      nameof<TransportRequestForm>((i) => i.StreetAndNumber),
      nameof<TransportRequestForm>((i) => i.PostalCode),
      nameof<TransportRequestForm>((i) => i.County),
      nameof<TransportRequestForm>((i) => i.Country),
      nameof<TransportRequestForm>((i) => i.ContactName),
      nameof<TransportRequestForm>((i) => i.Email),
      nameof<TransportRequestForm>((i) => i.Phone),
    ];
    if (partnerValues) {
      fieldsToPopulate.forEach((fieldName: Path<TransportRequestForm>) =>
        setValue(
          fieldName,
          partnerValues[fieldName] ?? defaultTransportRequestForm[fieldName],
          {
            shouldValidate: true,
            shouldDirty: true,
          }
        )
      );
      populateCityValueFromPartner();
    }
  };

  return (
    <Modal
      closeIcon
      corner="sharp"
      divided
      onClose={onClose}
      open
      primaryButtonProps={{
        children: t("LABEL_SAVE"),
        onClick: handleSubmit(onSubmit),
        form: "partner_form",
        type: "submit",
      }}
      secondaryButtonProps={{ children: t("LABEL_CANCEL"), onClick: onClose }}
      title={title}
    >
      <Form gap="small" id="partner_form" key={selectedStopId} method="post">
        <div className="flex justify-between">
          <div className="flex justify-start gap-4 flex-wrap">
            {formValues.map((stopValues, index) => (
              <div key={stopValues.Id}>
                <Tag
                  color={
                    stopValues.Id === selectedStopId ? "primary" : "neutral-300"
                  }
                  onClick={() => {
                    handleStopChangeWithSave(stopValues.Id);
                  }}
                  onRemove={
                    index > 0
                      ? () => handleDeleteStop(stopValues.Id)
                      : undefined
                  }
                  size="medium"
                  variant="soft"
                >
                  {`${t("LABEL_STOP")} ${index + 1} `}
                </Tag>
              </div>
            ))}
          </div>
          <Link
            className="items-start whitespace-nowrap mt-2"
            disabled={formValues.length >= MAX_TRANSPORT_REQUEST_STOPS}
            icon={faPlus}
            noUnderline
            onClick={handleAddStop}
          >
            {t("LABEL_ADD_STOP")}
          </Link>
        </div>
        {formValues.length >= MAX_TRANSPORT_REQUEST_STOPS && (
          <div className="flex">
            <InlineAlert variant="info">
              {t("LABEL_MAX_NUMBER_OF_N_STOPS", {
                n: MAX_TRANSPORT_REQUEST_STOPS,
              })}
            </InlineAlert>
          </div>
        )}
        <Divider />
        <Link
          disabled={partnerValues === null}
          icon={faCopy}
          noUnderline
          onClick={onCopyDetailsFromPartner}
        >
          {type === BookingTemplateConstants.TRANSFER_ADDRESS_TYPE.FROM
            ? t("LABEL_COPY_DETAILS_FROM_SHIPPER")
            : t("LABEL_COPY_DETAILS_FROM_CONSIGNEE")}
        </Link>
        <Input
          errorMessage={getErrorMessage(
            nameof<TransportRequestForm>((i) => i.CompanyName),
            errors
          )}
          fullWidth
          hasError={errors.CompanyName != null}
          id="company_name"
          label={t("LABEL_COMPANY_NAME")}
          maxLength={BookingTemplateConstants.MAX_INPUT_LENGTH_GENERIC}
          placeholder={t("PLACEHOLDER_COMPANY_NAME")}
          required
          {...register("CompanyName")}
        />
        <Input
          fullWidth
          id="company_street"
          label={t("LABEL_STREET_AND_NUMBER")}
          maxLength={BookingTemplateConstants.MAX_INPUT_LENGTH_GENERIC}
          placeholder={t("PLACEHOLDER_STREET_AND_NUMBER")}
          {...register("StreetAndNumber")}
        />
        <Grid mdCol={2}>
          <Input
            fullWidth
            id="company_city"
            label={t("LABEL_TOWN_CITY")}
            maxLength={BookingTemplateConstants.MAX_INPUT_LENGTH_GENERIC}
            placeholder={t("PLACEHOLDER_TOWN_CITY")}
            {...register("City")}
          />
          <Input
            fullWidth
            id="company_postal_code"
            label={t("LABEL_POSTAL_CODE")}
            maxLength={BookingTemplateConstants.MAX_INPUT_LENGTH_POSTAL_CODE}
            placeholder={t("PLACEHOLDER_POSTAL_CODE")}
            {...register("PostalCode")}
          />
        </Grid>
        <Input
          fullWidth
          id="company_district"
          label={`${t("LABEL_DISTRICT")} / ${t("LABEL_COUNTY")} / ${t(
            "LABEL_POSTBOX"
          )}`}
          maxLength={BookingTemplateConstants.MAX_INPUT_LENGTH_GENERIC}
          placeholder={t("PLACEHOLDER_DISTRICT_COUNTY_POSTBOX")}
          {...register("County")}
        />
        <Input
          fullWidth
          id="company_country"
          label={t("LABEL_COUNTRY")}
          maxLength={BookingTemplateConstants.MAX_INPUT_LENGTH_GENERIC}
          placeholder={t("PLACEHOLDER_COUNTRY")}
          {...register("Country")}
        />
        <Divider />
        <Input
          fullWidth
          id="company_contact_person"
          label={t("LABEL_CONTACT_PERSON")}
          maxLength={BookingTemplateConstants.MAX_INPUT_LENGTH_GENERIC}
          placeholder={t("PLACEHOLDER_CONTACT_PERSON")}
          {...register("ContactName")}
        />
        <TextArea
          errorMessage={getErrorMessage(
            nameof<TransportRequestForm>((i) => i.Email),
            errors
          )}
          formatMaxCharactersText={(count: number) =>
            t("TEXT_N_CHARACTERS_LEFT", { count: count })
          }
          fullWidth
          hasError={errors.Email != null}
          helperMessage={t("INFO_MULTIPLE_EMAILS")}
          id="company_email"
          label={t("LABEL_EMAIL")}
          maxLength={BookingTemplateConstants.MAX_INPUT_LENGTH_EMAIL}
          nonResizable
          placeholder={t("PLACEHOLDER_EMAIL_MULTIPLE")}
          uncontrolledValue={watch("Email") ? watch("Email") : ""}
          {...register("Email")}
        />
        <Input
          errorMessage={getErrorMessage(
            nameof<TransportRequestForm>((i) => i.Phone),
            errors
          )}
          fullWidth
          hasError={errors.Phone != null}
          id="company_phone"
          label={t("LABEL_PHONE")}
          maxLength={BookingTemplateConstants.MAX_INPUT_LENGTH_PHONE}
          placeholder={t("PLACEHOLDER_PHONE")}
          {...register("Phone")}
        />
        <TextArea
          formatMaxCharactersText={(count: number) =>
            t("TEXT_N_CHARACTERS_LEFT", { count: count })
          }
          fullWidth
          id="transport-additional-info"
          label={t("LABEL_ADDITIONAL_INFORMATION")}
          maxLength={120}
          placeholder={t("PLACEHOLDER_ADDITIONAL_INFORMATION")}
          uncontrolledValue={
            watch("AdditionalAddressInfo") ? watch("AdditionalAddressInfo") : ""
          }
          {...register("AdditionalAddressInfo")}
        />
      </Form>
    </Modal>
  );
};

export default TransportRequestModal;
